/**
 * WHAT THE SOUND ACTUALLY IS, IN NUMBERS.
 *
 * -- WHY THIS FILE IS NOT OPTIONAL -----------------------------------------------
 *
 * The author of this lane cannot hear. Every sentence of the form "it sounds warm" is therefore
 * a guess wearing the costume of a result, and this run has a standing rule against exactly
 * that. So the lane measures instead: peak and RMS in dBFS, where the energy sits by band, the
 * spectral centroid, and - the one that matters most - whether the loop joins up.
 *
 * -- THE SEAM, AND A DETECTOR THAT CAN FAIL --------------------------------------
 *
 * A looping bed whose last sample does not join its first produces a step once per lap, and a
 * step is a click. It is the most likely defect in this lane by a distance, and it is invisible
 * to every other statistic here: a buffer with a hard click has a normal RMS, a normal centroid
 * and a normal band breakdown.
 *
 * The detector is the CIRCULAR SECOND DIFFERENCE, x[n+1] - 2x[n] + x[n-1] taken on the ring. A
 * step of size s at the loop point puts a value of about s into that sequence at exactly the
 * seam; everywhere else in a band-limited signal the second difference is tiny, because a signal
 * with no energy above a few kilohertz simply cannot change direction quickly. That gap is what
 * makes the test sharp: for a bed lowpassed at 900 Hz the interior second differences run around
 * two hundredths of the signal amplitude, so a seam carrying a full-amplitude step stands out by
 * a factor of fifty rather than by a factor of four.
 *
 * The first difference was tried first and rejected in this lane, with the measurement that
 * showed why: on a 528,000-sample buffer, a deliberately broken loop scored 1.4 - inside the
 * pass band of any reasonable fence - because the single sample the joint lands on happened to
 * be small. A detector whose verdict rides on one draw is not a detector. The second difference
 * has the same single-draw numerator and a denominator fifty times smaller, which is what turns
 * the same evidence into a decision.
 *
 * THIS PROBE CAN FIRE, AND THE TEST PROVES IT TWICE RATHER THAN ASSUMING IT. `sea.test.ts`
 * plants a step of known size in a clean buffer and asserts the ratio goes up by about that
 * factor, and separately runs the identical noise through the identical filters WITHOUT the
 * circular warm-up - the obvious, wrong way to build a loop - and asserts the same. A seam
 * detector that has never seen a seam is not evidence.
 *
 * -- WHAT THE ABSENCE CLAIM IS WORTH ---------------------------------------------
 *
 * "No click" means: the second difference at the loop point is not distinguishable from the
 * N - 4 interior ones. The sample size is that count and it is printed with every result. The
 * comparison is against a percentile rather than against a maximum, so one stray interior
 * sample cannot raise the bar the seam has to clear.
 *
 * -- AND ONE APPARATUS CORRECTION, RECORDED RATHER THAN QUIETLY FIXED ------------
 *
 * The spectral centroid here is POWER-weighted, not magnitude-weighted, and that is a decision
 * this file got wrong first. The textbook centroid weights by |X|, and on a signal with a 12 dB
 * per octave roll-off that integral diverges logarithmically in the tail: measured on this
 * lane's own bed, the magnitude centroid read 3,370 Hz while 97.9% of the POWER sat below 4 kHz
 * and 24% of it below 125 Hz. The number was a property of how the sum was taken, not of the
 * sound. Power weighting agrees with the band table, which is the measurement that is actually
 * load-bearing here, and `rolloff85Hz` is reported beside it as the more robust statistic of the
 * two.
 */

import { magnitudeSpectrum } from './fft';

/** Everything worth knowing about one rendered signal. */
export interface SignalReport {
  readonly samples: number;
  readonly sampleRate: number;
  readonly seconds: number;
  readonly peak: number;
  readonly peakDbfs: number;
  readonly rms: number;
  readonly rmsDbfs: number;
  /** Peak over RMS in dB. Under about 10 dB a bed is squashed; over about 20 it is spiky. */
  readonly crestDb: number;
  /** POWER-weighted mean frequency, Hz. See the apparatus note at the head of this file. */
  readonly centroidHz: number;
  /** The frequency below which 85% of the power sits. The robust brightness statistic. */
  readonly rolloff85Hz: number;
  /** Fraction of total power in each band of BAND_EDGES, summing to 1. */
  readonly bands: readonly number[];
  readonly seam: SeamReport;
}

export interface SeamReport {
  /** |x[0] - x[N-1]|: the raw step at the joint, reported because it is what a reader pictures. */
  readonly seamStep: number;
  /** The largest circular second difference at the two samples the loop point touches. */
  readonly seamCurve: number;
  /** The 99.9th percentile of the interior second differences. */
  readonly curveP999: number;
  /** The largest interior second difference anywhere in the buffer. */
  readonly curveMax: number;
  /** seamCurve / curveP999. One means the loop point is an ordinary boundary. */
  readonly ratio: number;
  /** The same in dB, which is the form the ear cares about. */
  readonly excessDb: number;
  /** How many interior points the percentile was taken over. The absence claim's sample size. */
  readonly boundaries: number;
}

/** Band edges in Hz. Nine bands, roughly octave-wide where hearing is sharpest. */
export const BAND_EDGES: readonly number[] = [0, 60, 125, 250, 500, 1000, 2000, 4000, 8000, Infinity];

const dbfs = (x: number): number => (x > 0 ? 20 * Math.log10(x) : -Infinity);

/**
 * The seam, for one channel.
 *
 * The percentile is taken over the interior boundaries only - n-1 of them - so the seam is
 * being compared against a population it is not itself a member of. Including it would let a
 * bad enough click raise its own threshold, which is the sort of self-flattering measurement
 * this run has been caught making before.
 */
export function measureSeam(ch: Float32Array): SeamReport {
  const n = ch.length;
  if (n < 8) {
    return { seamStep: 0, seamCurve: 0, curveP999: 0, curveMax: 0, ratio: 0, excessDb: -Infinity, boundaries: 0 };
  }
  const curve = (i: number): number =>
    Math.abs((ch[(i + 1) % n] as number) - 2 * (ch[i] as number) + (ch[(i - 1 + n) % n] as number));

  // The interior population: every point that does not touch the joint. Excluding the seam from
  // its own reference is the whole reason the comparison means anything - a bad enough click
  // included in the population would quietly raise the threshold it has to clear.
  const interior = new Float64Array(n - 4);
  let max = 0;
  for (let i = 2; i < n - 2; i++) {
    const d = curve(i);
    interior[i - 2] = d;
    if (d > max) max = d;
  }
  interior.sort();
  const p999 = interior[Math.min(interior.length - 1, Math.floor(interior.length * 0.999))] as number;
  // Both points the joint touches: sample 0 reads back to N-1, and N-1 reads forward to 0.
  const seamCurve = Math.max(curve(0), curve(n - 1));
  const ratio = p999 > 0 ? seamCurve / p999 : 0;
  return {
    seamStep: Math.abs((ch[0] as number) - (ch[n - 1] as number)),
    seamCurve,
    curveP999: p999,
    curveMax: max,
    ratio,
    excessDb: dbfs(ratio),
    boundaries: interior.length,
  };
}

/**
 * THE SAME DETECTOR, POINTED AT AN INSTANT INSIDE A LONGER SIGNAL.
 *
 * `measureSeam` asks whether a buffer joins to itself. This asks whether a RENDERED capture has
 * anything at the instant the loop turned over - which is the question that matters once the
 * browser is in the path, because between the generator and the ear there is a buffer source, a
 * possible resample and a loop implementation, and any of the three could put a step in a signal
 * that measured clean in Node. The two are reported separately in this round for exactly that
 * reason: they can disagree, and the disagreement is the interesting case.
 *
 * The population excludes a window either side of the instant under test, so a real click cannot
 * raise the threshold it is being judged against.
 */
export function measureJoinAt(ch: Float32Array, index: number, guard = 64): SeamReport {
  const n = ch.length;
  const curve = (i: number): number =>
    Math.abs((ch[i + 1] as number) - 2 * (ch[i] as number) + (ch[i - 1] as number));
  if (n < 4 * guard + 8 || index < 2 || index >= n - 2) {
    return { seamStep: 0, seamCurve: 0, curveP999: 0, curveMax: 0, ratio: 0, excessDb: -Infinity, boundaries: 0 };
  }
  const interior: number[] = [];
  let max = 0;
  for (let i = 2; i < n - 2; i++) {
    if (Math.abs(i - index) <= guard) continue;
    const d = curve(i);
    interior.push(d);
    if (d > max) max = d;
  }
  const sorted = Float64Array.from(interior).sort();
  const p999 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.999))] as number;
  let seamCurve = 0;
  for (let i = index - 2; i <= index + 2; i++) seamCurve = Math.max(seamCurve, curve(i));
  const ratio = p999 > 0 ? seamCurve / p999 : 0;
  return {
    seamStep: Math.abs((ch[index] as number) - (ch[index - 1] as number)),
    seamCurve,
    curveP999: p999,
    curveMax: max,
    ratio,
    excessDb: dbfs(ratio),
    boundaries: sorted.length,
  };
}

/**
 * The spectrum, averaged over blocks spread evenly across the whole buffer.
 *
 * Averaging matters: one block of a signal that breathes catches whatever the breath was doing
 * at that instant, and the bed is designed to breathe. Sixteen blocks spread across the loop
 * average over a whole period of every envelope in it.
 */
function averageSpectrum(ch: Float32Array, blockSize: number, blocks: number): Float64Array {
  const usable = Math.max(1, ch.length - blockSize);
  const acc = new Float64Array(blockSize / 2 + 1);
  let taken = 0;
  for (let b = 0; b < blocks; b++) {
    const start = Math.floor((usable * b) / blocks);
    if (start + blockSize > ch.length) break;
    const mag = magnitudeSpectrum(ch.subarray(start, start + blockSize));
    for (let k = 0; k < acc.length; k++) acc[k] = (acc[k] as number) + (mag[k] as number) ** 2;
    taken++;
  }
  if (taken > 0) for (let k = 0; k < acc.length; k++) acc[k] = Math.sqrt((acc[k] as number) / taken);
  return acc;
}

/** One channel, end to end. `blockSize` must be a power of two. */
export function measureSignal(ch: Float32Array, sampleRate: number, blockSize = 8192, blocks = 16): SignalReport {
  let peak = 0;
  let sumSq = 0;
  for (let i = 0; i < ch.length; i++) {
    const v = ch[i] as number;
    const a = Math.abs(v);
    if (a > peak) peak = a;
    sumSq += v * v;
  }
  const rms = Math.sqrt(sumSq / Math.max(1, ch.length));
  const spec = averageSpectrum(ch, blockSize, blocks);
  const binHz = sampleRate / blockSize;
  let weighted = 0;
  let total = 0;
  const perBin = new Float64Array(spec.length);
  const bandPower = new Array<number>(BAND_EDGES.length - 1).fill(0);
  for (let k = 1; k < spec.length; k++) {
    const hz = k * binHz;
    // POWER, not magnitude. See the apparatus note at the head of this file: the
    // magnitude-weighted centroid diverges logarithmically in a 12 dB per octave tail and
    // read 3,370 Hz on a bed with 97.9% of its power under 4 kHz.
    const power = (spec[k] as number) ** 2;
    perBin[k] = power;
    weighted += hz * power;
    total += power;
    for (let b = 0; b < bandPower.length; b++) {
      if (hz >= (BAND_EDGES[b] as number) && hz < (BAND_EDGES[b + 1] as number)) {
        bandPower[b] = (bandPower[b] as number) + power;
        break;
      }
    }
  }
  const totalPower = bandPower.reduce((a, b) => a + b, 0);
  // The 85% rolloff: walk up the bins until that share of the power is behind you.
  let running = 0;
  let rolloff = 0;
  for (let k = 1; k < perBin.length; k++) {
    running += perBin[k] as number;
    if (running >= total * 0.85) {
      rolloff = k * binHz;
      break;
    }
  }
  return {
    samples: ch.length,
    sampleRate,
    seconds: ch.length / sampleRate,
    peak,
    peakDbfs: dbfs(peak),
    rms,
    rmsDbfs: dbfs(rms),
    crestDb: dbfs(peak) - dbfs(rms),
    centroidHz: total > 0 ? weighted / total : 0,
    rolloff85Hz: rolloff,
    bands: bandPower.map((p) => (totalPower > 0 ? p / totalPower : 0)),
    seam: measureSeam(ch),
  };
}

/** Human-readable band labels, in the same order as `bands`. */
export function bandLabels(): string[] {
  const out: string[] = [];
  for (let b = 0; b < BAND_EDGES.length - 1; b++) {
    const lo = BAND_EDGES[b] as number;
    const hi = BAND_EDGES[b + 1] as number;
    out.push(hi === Infinity ? `${lo}+ Hz` : `${lo}-${hi} Hz`);
  }
  return out;
}

/**
 * Sum a set of channel sets into one interleaved-by-channel mix, the way the graph does.
 *
 * Used to report the SUMMED bed rather than each layer alone: the number a player is exposed to
 * is the sum, and reporting only the parts is how a mix ends up 3 dB hotter than anybody said.
 * A one-channel layer is spread to every output channel, which is what the Web Audio API does
 * with a mono buffer on a stereo destination.
 */
export function mixLayers(layers: readonly (readonly Float32Array[])[], channels: number, length: number): Float32Array[] {
  const out: Float32Array[] = [];
  for (let c = 0; c < channels; c++) out.push(new Float32Array(length));
  for (const layer of layers) {
    for (let c = 0; c < channels; c++) {
      const src = layer.length === 1 ? (layer[0] as Float32Array) : (layer[c % layer.length] as Float32Array);
      const dst = out[c] as Float32Array;
      for (let i = 0; i < length; i++) dst[i] = (dst[i] as number) + (src[i % src.length] as number);
    }
  }
  return out;
}
